// Design v9 helpers (reference logic.js 9.0): project comments as a pseudo task `pt-{projectId}` (reference ptask /
// blankPTask / locate / findTask), and the "Updated" sort's latest change of a task (reference lastUpd / updLine).
// Production keeps project comments on the project itself (`project.comments`, synced with addProjectComment /
// deleteProjectComment); the pseudo task shares that array, so the comment code paths of tasks work unchanged.
import type { Comment, LogEntry, Project, Task, TaskList } from '@/domain/model';
import { createdAt, rel } from './constants';
import { t as tr } from '@/app/i18n';

export const PT_PREFIX = 'pt-';
export const isPTask = (id: unknown): id is string => typeof id === 'string' && id.startsWith(PT_PREFIX);

/** The project's comment thread as a task-shaped object; `comments` is the project's own array (mutations land there). */
export function ptaskOf(p: Project): Task {
  if (!p.comments) p.comments = [];
  return { id: PT_PREFIX + p.id, title: p.name, tags: [], checklist: [], comments: p.comments, files: p.files || [], log: [], done: false } as unknown as Task;
}
export const ptList = (title = ''): TaskList => ({ id: '', title, tasks: [] } as unknown as TaskList);

/** Updates › Notifications rows for project comments (reference myNotifs, v9: every member, "on the project"). */
export function projectCommentNotifs(visible: Project[], me: string): Array<{ p: Project; l: TaskList; t: Task; c: Comment; why: 'on the project' }> {
  const rows: Array<{ p: Project; l: TaskList; t: Task; c: Comment; why: 'on the project' }> = [];
  for (const p of visible) {
    if (!(p.members || []).includes(me)) continue;
    for (const c of p.comments || []) if (c.by !== me) rows.push({ p, l: ptList(tr('Project discussion')), t: ptaskOf(p), c, why: 'on the project' });
  }
  return rows;
}

/** Updates › Mentions rows for project comments: comments by others that mention me (the reference reads ptask.log). */
export function projectCommentMentions(visible: Project[], me: string, mentionsIn: (text: string) => string[], nameOf: (id: string) => string): Array<{ p: Project; l: TaskList; t: Task; e: LogEntry }> {
  const rows: Array<{ p: Project; l: TaskList; t: Task; e: LogEntry }> = [];
  for (const p of visible) for (const c of p.comments || []) {
    if (c.by === me || !mentionsIn(c.text).includes(me)) continue;
    rows.push({ p, l: ptList(tr('Project discussion')), t: ptaskOf(p), e: { k: 'mention', to: me, by: c.by, at: c.at, text: 'mentioned ' + nameOf(me) + ' in a comment' } as LogEntry });
  }
  return rows;
}

/** Latest change of a task for the Updated sort (reference lastUpd): a log entry or a comment, whichever is newest. */
export function lastUpd(t: Task): { at: number; by: string | null; text: string } | null {
  let best: { at: number; by: string | null; text: string | null } | null = null;
  const consider = (at: number | undefined, by: string | null | undefined, text: string | null) => { if (at && (!best || at > best.at)) best = { at, by: by ?? null, text }; };
  (t.log || []).forEach(e => { if (e.k === 'mention') return; consider(e.at, e.by, e.k === 'create' ? tr('added this task') : e.k === 'comment' ? null : (e.text || tr('changed this task'))); });
  (t.comments || []).forEach(c => consider(c.at, c.by, tr('commented') + ': “' + (c.text || '').replace(/\s+/g, ' ').slice(0, 60) + ((c.text || '').length > 60 ? '…' : '') + '”'));
  if (!best) { const c = createdAt(t); if (c) best = { at: c, by: null, text: tr('added this task') }; }
  const b = best as { at: number; by: string | null; text: string | null } | null;
  if (b && !b.text) { const c = (t.comments || []).filter(x => x.at === b.at)[0]; b.text = c ? tr('commented') + ': “' + c.text.slice(0, 60) + '”' : tr('commented'); }
  return b as { at: number; by: string | null; text: string } | null;
}
/** "Sara commented: “…” · 2h" (reference updLine). */
export function updLine(t: Task, who: (id: string) => string): string {
  const u = lastUpd(t); if (!u) return '';
  const w = u.by ? who(u.by) : '';
  return (w ? w + ' ' : '') + u.text + ' · ' + rel(u.at);
}
